"use client"

import {
    ArrowRight,
    BookOpen,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Clock,
    Sparkles
} from "lucide-react"
import { motion } from "motion/react"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useRef, useState } from "react"

import { cn } from "@/core/utils/utils"
import { blogPosts } from "@/data/blog"
import { Post } from "@/types/post"

export default function BlogPreview() {
    const containerRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)
    const [activeIndex, setActiveIndex] = useState(0)

    // Select the latest 5 posts for the preview
    // We filter for posts that have accentColor/gradient defined to ensure they render correctly in this view
    const posts = blogPosts
        .filter(p => p.accentColor && p.gradient)
        .slice(0, 5)

    const checkScroll = useCallback(() => {
        if (!scrollRef.current) return
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
        setCanScrollLeft(scrollLeft > 10)
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10)

        // Calculate active index based on scroll position
        const cardWidth = 380 + 24 // card width + gap
        const newIndex = Math.round(scrollLeft / cardWidth)
        setActiveIndex(Math.min(newIndex, posts.length - 1))
    }, [posts.length])

    useEffect(() => {
        const scrollElement = scrollRef.current
        if (scrollElement) {
            let rafId: number | null = null;

            const onScroll = () => {
                if (rafId) return;
                rafId = requestAnimationFrame(() => {
                    checkScroll();
                    rafId = null;
                });
            };

            scrollElement.addEventListener('scroll', onScroll, { passive: true })
            checkScroll()

            return () => {
                scrollElement.removeEventListener('scroll', onScroll)
                if (rafId) cancelAnimationFrame(rafId)
            }
        }
    }, [checkScroll])

    const scroll = (direction: 'left' | 'right') => {
        if (!scrollRef.current) return
        const cardWidth = 380 + 24
        const scrollAmount = direction === 'left' ? -cardWidth : cardWidth
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }

    const scrollToIndex = (index: number) => {
        if (!scrollRef.current) return
        const cardWidth = 380 + 24
        scrollRef.current.scrollTo({ left: index * cardWidth, behavior: 'smooth' })
    }

    return (
        <section
            id="blog"
            className="relative py-32 overflow-hidden"
            ref={containerRef}
        >
            {/* Premium Background */}
            <div className="absolute inset-0 bg-background" style={{ contain: 'paint' }}>
                {/* Gradient Orbs */}
                <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-violet-500/3 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-emerald-500/3 rounded-full blur-[120px]" />

                {/* Subtle Pattern */}
                <div
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)`,
                        backgroundSize: '32px 32px',
                    }}
                />

                {/* Top Edge */}
                <div className="absolute top-0 inset-x-0 h-px bg-linear-to-r from-transparent via-white/6 to-transparent" />
            </div>

            <div className="relative z-10">
                {/* Header */}
                <div className="container px-4 md:px-6 mb-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        >
                            {/* Title */}
                            <h2 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-white mb-4">
                                Latest{" "}
                                <span className="bg-linear-to-r from-white via-white/90 to-white/60 bg-clip-text text-transparent">
                                    Insights
                                </span>
                            </h2>

                            {/* Subtitle */}
                            <p className="text-lg text-white/40 max-w-xl leading-relaxed">
                                Unfiltered takes on architecture, leadership, and the technologies that matter.
                                <span className="text-white/60"> No fluff.</span>
                            </p>
                        </motion.div>

                        {/* Navigation Controls */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="flex items-center gap-3"
                        >
                            <button
                                onClick={() => scroll('left')}
                                disabled={!canScrollLeft}
                                className={cn(
                                    "w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300",
                                    canScrollLeft
                                        ? "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30 text-white"
                                        : "border-white/6 bg-white/2 text-white/20 cursor-not-allowed"
                                )}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => scroll('right')}
                                disabled={!canScrollRight}
                                className={cn(
                                    "w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-300",
                                    canScrollRight
                                        ? "border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30 text-white"
                                        : "border-white/6 bg-white/2 text-white/20 cursor-not-allowed"
                                )}
                                aria-label="Scroll right"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </motion.div>
                    </div>
                </div>

                {/* Carousel */}
                <div className="relative">
                    {/* Left Fade */}
                    <div className="absolute left-0 top-0 bottom-0 w-20 bg-linear-to-r from-[#08080a] to-transparent z-10 pointer-events-none" />

                    {/* Right Fade */}
                    <div className="absolute right-0 top-0 bottom-0 w-20 bg-linear-to-l from-[#08080a] to-transparent z-10 pointer-events-none" />

                    {/* Scrollable Container */}
                    <div
                        ref={scrollRef}
                        className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory px-4 md:px-6 lg:px-8 pb-4"
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        {posts.map((post, index) => (
                            <motion.div
                                key={post.slug}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.1,
                                    ease: [0.16, 1, 0.3, 1]
                                }}
                                className="snap-start"
                            >
                                <BlogCard post={post} index={index} />
                            </motion.div>
                        ))}

                        {/* View All Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: posts.length * 0.1 }}
                            className="snap-start"
                        >
                            <Link
                                href="/blog"
                                className="group flex flex-col items-center justify-center w-[380px] h-[480px] rounded-3xl border border-dashed border-white/10 bg-white/1 hover:bg-white/3 hover:border-white/15 transition-all duration-500 shrink-0"
                            >
                                <div className="w-16 h-16 rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-white/10 transition-all duration-300">
                                    <ArrowRight className="w-6 h-6 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                                </div>
                                <span className="text-xl font-semibold text-white/60 group-hover:text-white transition-colors">
                                    View All Articles
                                </span>
                                <span className="text-sm text-white/30 mt-2">
                                    Explore the archive
                                </span>
                            </Link>
                        </motion.div>
                    </div>
                </div>

                {/* Pagination Dots */}
                <div className="container px-4 md:px-6 mt-8">
                    <div className="flex items-center justify-center gap-2">
                        {posts.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => scrollToIndex(index)}
                                className="p-2 group focus:outline-none"
                                aria-label={`Go to blog article ${index + 1}`}
                            >
                                <div
                                    className={cn(
                                        "transition-all duration-300 rounded-full",
                                        index === activeIndex
                                            ? "w-8 h-2 bg-white/60"
                                            : "w-2 h-2 bg-white/20 group-hover:bg-white/30"
                                    )}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

// Premium Blog Card
function BlogCard({ post, index }: { post: Post, index: number }) {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
    const cardRef = useRef<HTMLDivElement>(null)

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!cardRef.current) return
        const rect = cardRef.current.getBoundingClientRect()
        setMousePosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        })
    }, [])

    const accentColors: Record<string, { bg: string; text: string; border: string }> = {
        orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20" },
        violet: { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/20" },
        emerald: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20" },
        blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20" },
        pink: { bg: "bg-pink-500/10", text: "text-pink-400", border: "border-pink-500/20" },
    }

    const accent = (post.accentColor && accentColors[post.accentColor]) || accentColors.violet

    return (
        <Link href={post.slug} className="block">
            <div
                ref={cardRef}
                onMouseMove={handleMouseMove}
                className={cn(
                    "group relative w-[380px] h-[480px] rounded-3xl overflow-hidden shrink-0",
                    "border border-white/6 hover:border-white/12 transition-all duration-500",
                    "bg-white/2 backdrop-blur-xl"
                )}
            >
                {/* Spotlight Effect */}
                <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(500px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(255,255,255,0.04), transparent 40%)`
                    }}
                />

                {/* Image Container */}
                <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-linear-to-b from-transparent via-transparent to-[#08080a] z-10" />
                    <Image
                        src={post.coverImage || "/placeholder.svg"}
                        alt={post.title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover transition-transform duration-700 group-hover:scale-110"
                    />

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
                        <span className={cn(
                            "px-3 py-1.5 rounded-full text-xs font-semibold backdrop-blur-md",
                            accent.bg,
                            accent.text,
                            "border",
                            accent.border
                        )}>
                            {post.category}
                        </span>
                        {post.tags && post.tags.length > 0 && (
                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 backdrop-blur-md border border-white/10 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                {post.tags[0]}
                            </span>
                        )}
                    </div>

                    {/* Number Indicator */}
                    <div className="absolute top-4 right-4 z-20">
                        <span className="text-[10px] font-mono text-white/30">
                            {String(index + 1).padStart(2, '0')}
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div className="relative z-10 p-6 flex flex-col h-[calc(100%-192px)]">
                    {/* Meta */}
                    <div className="flex items-center gap-3 text-xs text-white/40 mb-4">
                        <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {post.date}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-white/20" />
                        <span className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {post.readTime}
                        </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-white mb-3 leading-tight group-hover:text-white transition-colors line-clamp-2">
                        {post.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-sm text-white/40 leading-relaxed line-clamp-3 flex-1">
                        {post.excerpt}
                    </p>

                    {/* Read More */}
                    <div className="mt-auto pt-4 flex items-center justify-between">
                        <span className={cn(
                            "text-sm font-medium flex items-center gap-2 transition-colors",
                            accent.text
                        )}>
                            Read Article
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>

                        {/* Reading Progress Hint */}
                        <div className="flex items-center gap-1">
                            {[...Array(3)].map((_, i) => (
                                <div
                                    key={i}
                                    className={cn(
                                        "w-1.5 h-1.5 rounded-full",
                                        i === 0 ? accent.bg.replace('/10', '/40') : "bg-white/10"
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Gradient Accent */}
                <div
                    className={cn(
                        "absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500",
                        post.gradient
                    )}
                />
            </div>
        </Link>
    )
}
